import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AssignmentApi, EndeavorApi } from '../services/ApiService';
import { useData } from '../contexts/DataContext';
import { useToast } from '../contexts/ToastContext';
import './AssignmentView.css';
import AssignmentDetails from '../components/assignments/AssignmentDetails';

function AssignmentView() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { clearCache, deleteAssignment } = useData();
  const { addToast } = useToast();
  
  const [assignment, setAssignment] = useState(null);
  const [endeavor, setEndeavor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch assignment and endeavor data
  const fetchAssignmentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('AssignmentView: Fetching assignment data for ID:', assignmentId);

      // Fetch assignment details
      const assignmentResponse = await AssignmentApi.getAssignment(assignmentId);
      if (!assignmentResponse.ok) {
        throw new Error(`Failed to fetch assignment: ${assignmentResponse.status}`);
      }

      const assignmentData = await assignmentResponse.json();
      console.log('AssignmentView: Assignment data:', assignmentData);
      setAssignment(assignmentData);

      // Fetch endeavor data if assignment has endeavorId
      if (assignmentData.endeavorId) {
        try {
          const endeavorResponse = await EndeavorApi.getEndeavor(assignmentData.endeavorId);
          if (endeavorResponse.ok) {
            const endeavorData = await endeavorResponse.json();
            console.log('AssignmentView: Endeavor data:', endeavorData);
            setEndeavor(endeavorData);
          }
        } catch (endeavorError) {
          console.warn('AssignmentView: Error fetching endeavor:', endeavorError);
          // Don't fail the whole page if endeavor can't be loaded
        }
      }

    } catch (error) {
      console.error('AssignmentView: Error fetching assignment data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    if (assignmentId) {
      fetchAssignmentData();
    }
  }, [assignmentId, fetchAssignmentData]);

  // Handle actions
  const handleEdit = useCallback(() => {
    navigate(`/assignment/${assignmentId}/edit`);
  }, [assignmentId, navigate]);

  const handleToggleCompleted = useCallback(async () => {
    try {
      const newCompletedStatus = !assignment.completed;
      
      // Update the assignment with the new completion status
      const updateData = {
        ...assignment,
        completed: newCompletedStatus,
        status: newCompletedStatus ? 'Completed' : 'Pending'
      };

      const response = await AssignmentApi.updateAssignment(assignmentId, updateData);
      if (response.ok) {
        setAssignment(prev => ({ 
          ...prev, 
          completed: newCompletedStatus,
          status: newCompletedStatus ? 'Completed' : 'Pending'
        }));
        
        // Clear cache to refresh dashboard data
        clearCache('assignment');
        clearCache('user-endeavors');
      } else {
        throw new Error(`Failed to update assignment: ${response.status}`);
      }
    } catch (error) {
      console.error('Error updating assignment completion status:', error);
      alert('Failed to update assignment status');
    }
  }, [assignmentId, assignment, clearCache]);

  const handleGoToEndeavor = useCallback(() => {
    if (endeavor?.endeavorId || endeavor?.id) {
      navigate(`/course/${endeavor.endeavorId || endeavor.id}`);
    }
  }, [endeavor, navigate]);

  if (loading) {
    return (
      <div className="assignment-view-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading assignment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assignment-view-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Assignment</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
            <button 
              className="btn btn-primary"
              onClick={fetchAssignmentData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="assignment-view-container">
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h2>Assignment Not Found</h2>
          <p>The assignment you're looking for doesn't exist or has been deleted.</p>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assignment-view-container">
      <AssignmentDetails
        assignment={assignment}
        endeavor={endeavor}
        onMarkComplete={handleToggleCompleted}
        onEdit={handleEdit}
        navigateToEndeavor={handleGoToEndeavor}
        navigateToEvent={() => navigate(`/event/${assignment.eventId}`)}
        onDelete={async (assignmentId) => {
          await deleteAssignment(assignmentId);
          addToast('Assignment deleted successfully', 'success');
          navigate('/dashboard');
        }}
      />
    </div>
  );
}

export default AssignmentView;
